import Phaser from 'phaser';
import { sceneAfter } from '../game/scenes';
import { addHeroFramesToTexture, HERO_IDLE_FRAME } from '../game/heroFrames';
import { addCoverImage, setImageHeight } from '../game/sceneArt';
import { attachKeyboard, createEmptyControls, type ControlState } from '../input/KeyboardAdapter';
import { createVirtualControls } from '../input/VirtualControls';
import { createIconHud } from '../ui/IconHud';

export class PadScene extends Phaser.Scene {
  private controls!: ControlState;
  private player!: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody;
  private rocketZone!: Phaser.GameObjects.Rectangle;
  private hint!: Phaser.GameObjects.Text;
  private sequenceLocked = false;
  private animTick = 0;
  private frame = 0;
  private rocketImg!: Phaser.GameObjects.Image;

  constructor() {
    super('Pad');
  }

  preload() {
    this.load.image('rocket_pad', 'assets/rocket-pad-v3.png');
    this.load.image('launch_pad', 'assets/launch-pad.png');
    this.load.image('hero_sheet', 'assets/hero-sheet-v3.png');
    this.load.image('earth_bg', 'assets/earthbg.png');
  }

  create() {
    createIconHud(this);
    this.controls = createEmptyControls();
    attachKeyboard(this, this.controls);
    createVirtualControls(this, this.controls);

    this.physics.world.gravity.y = 960;
    const { width, height } = this.scale;
    const groundY = height - 34;

    addCoverImage(this, 'earth_bg', -20);

    addHeroFramesToTexture(this, 'hero_sheet');

    const ground = this.add.rectangle(width / 2, groundY + 20, width, 56, 0x000000, 0);
    this.physics.add.existing(ground, true);

    const rocketX = width * 0.75;

    const pad = this.add.image(rocketX, groundY, 'launch_pad').setOrigin(0.5, 1).setDepth(10);
    setImageHeight(pad, 80);

    this.rocketImg = setImageHeight(
      this.add.image(rocketX, groundY - pad.displayHeight + 30, 'rocket_pad').setOrigin(0.5, 1),
      220,
    ).setDepth(10);

    this.rocketZone = this.add.rectangle(rocketX, groundY - 60, 120, 120, 0x000000, 0);

    this.player = this.physics.add.sprite(112, groundY - 64, 'hero_sheet', HERO_IDLE_FRAME).setDepth(14);
    this.player.setCollideWorldBounds(true);
    this.player.body.setSize(30, 128);
    this.player.body.setOffset(27, 91);

    this.hint = this.add.text(22, 22, 'Camina al cohete y pulsa espacio para entrar.', {
      fontFamily: 'monospace',
      fontSize: '17px',
      color: '#eef6ff',
      wordWrap: { width: Math.min(420, width - 44) },
    }).setDepth(2000).setScrollFactor(0);

    this.physics.add.collider(this.player, ground);
  }

  update(_time: number, dt: number) {
    if (this.sequenceLocked) {
      this.player.setVelocity(0, 0);
      return;
    }

    const speed = 208;
    const onGround = this.player.body.blocked.down || this.player.body.touching.down;
    const nearRocket = Phaser.Geom.Intersects.RectangleToRectangle(
      this.player.getBounds(),
      this.rocketZone.getBounds(),
    );

    if (this.controls.jump && onGround) {
      this.player.setVelocityY(-420);
    }

    if (this.controls.left) {
      this.player.setVelocityX(-speed);
      this.player.setFlipX(true);
    } else if (this.controls.right) {
      this.player.setVelocityX(speed);
      this.player.setFlipX(false);
    } else {
      this.player.setVelocityX(0);
    }

    if (nearRocket && this.controls.action) {
      this.enterRocket();
      return;
    }

    if (nearRocket) {
      this.hint.setText('Pulsa espacio para entrar al cohete.');
    } else {
      this.hint.setText('Camina al cohete y pulsa espacio para entrar.');
    }

    this.animTick += dt;
    if (this.animTick > 130 && (this.controls.left || this.controls.right) && onGround) {
      this.animTick = 0;
      this.frame = (this.frame + 1) % 5;
      this.player.setTexture('hero_sheet', `hero_${this.frame}`);
    }

    if (!this.controls.left && !this.controls.right) {
      this.player.setTexture('hero_sheet', HERO_IDLE_FRAME);
    }
  }

  private enterRocket() {
    this.sequenceLocked = true;
    this.hint.setText('Entrando al cohete...');
    this.player.setVelocity(0, 0);
    this.player.body.enable = false;

    this.tweens.add({
      targets: this.player,
      x: this.rocketImg.x,
      y: this.rocketImg.y - this.rocketImg.displayHeight * 0.4,
      alpha: 0.04,
      duration: 800,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        const next = sceneAfter('Pad');
        if (next) {
          this.scene.start(next);
        }
      },
    });
  }
}
